import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Firestore,
  type WriteBatch,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable, type Functions } from "firebase/functions";
import { createPageSchema } from "@/lib/validation/schemas";
import { normalizeText } from "@/lib/utils";
import { tokenizeSearch } from "@/lib/pages/page-tree";
import type {
  DocumentationPage,
  PagePermission,
  PageVersion,
  RichTextDocument,
} from "@/types/domain";

const blankContent: RichTextDocument = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export interface ActivityActor {
  id: string;
  name: string;
}

export function pageActivityActor(user: {
  uid: string;
  displayName?: string | null;
  email?: string | null;
}): ActivityActor {
  return {
    id: user.uid,
    name: user.displayName ?? user.email ?? "Group member",
  };
}

type PageActivityAction =
  | "page.created"
  | "page.renamed"
  | "page.published"
  | "page.draft"
  | "page.archived"
  | "page.deleted"
  | "page.restored";

function addPageActivityToBatch(
  batch: WriteBatch,
  db: Firestore,
  groupId: string,
  pageId: string,
  title: string,
  actor: ActivityActor,
  action: PageActivityAction,
) {
  const activityRef = doc(collection(db, "groups", groupId, "activities"));
  batch.set(activityRef, {
    id: activityRef.id,
    groupId,
    actorId: actor.id,
    actorName: actor.name,
    action,
    entityType: "page",
    entityId: pageId,
    entityName: title,
    createdAt: serverTimestamp(),
  });
}

interface SerializedPage
  extends Omit<
    DocumentationPage,
    "createdAt" | "updatedAt" | "publishedAt"
  > {
  createdAtMs: number;
  updatedAtMs: number;
  publishedAtMs?: number;
}

function hydratePage(page: SerializedPage): DocumentationPage {
  return {
    ...page,
    createdAt: Timestamp.fromMillis(page.createdAtMs),
    updatedAt: Timestamp.fromMillis(page.updatedAtMs),
    ...(page.publishedAtMs
      ? { publishedAt: Timestamp.fromMillis(page.publishedAtMs) }
      : {}),
  };
}

export async function listPages(functions: Functions, groupId: string) {
  const response = await httpsCallable<
    { groupId: string },
    { pages: SerializedPage[] }
  >(functions, "listAccessiblePages")({ groupId });
  return response.data.pages.map(hydratePage);
}

export async function listPagesDirect(db: Firestore, groupId: string) {
  const snapshot = await getDocs(
    query(
      collection(db, "groups", groupId, "pages"),
      where("status", "in", ["draft", "published"]),
    ),
  );
  return snapshot.docs
    .map((item) => item.data() as DocumentationPage)
    .sort((a, b) => a.order - b.order);
}

export async function listTrash(db: Firestore, groupId: string) {
  const snapshot = await getDocs(
    query(
      collection(db, "groups", groupId, "pages"),
      where("status", "==", "deleted"),
      orderBy("updatedAt", "desc"),
      limit(100),
    ),
  );
  return snapshot.docs.map((item) => item.data() as DocumentationPage);
}

export async function getPage(
  db: Firestore,
  groupId: string,
  pageId: string,
) {
  const snapshot = await getDoc(doc(db, "groups", groupId, "pages", pageId));
  return snapshot.exists() ? (snapshot.data() as DocumentationPage) : null;
}

export async function createPage(
  db: Firestore,
  groupId: string,
  actor: ActivityActor,
  values: { title: string; parentPageId: string | null },
) {
  const input = createPageSchema.parse(values);
  const parent = input.parentPageId
    ? await getPage(db, groupId, input.parentPageId)
    : null;
  if (input.parentPageId && !parent) {
    throw new Error("The parent page no longer exists.");
  }
  const depth = parent ? parent.depth + 1 : 0;
  if (depth > 4) {
    throw new Error("Pages can be nested up to five levels.");
  }

  const nextOrder = Date.now();
  const pageRef = doc(collection(db, "groups", groupId, "pages"));
  const ancestorIds = parent ? [...parent.ancestorIds, parent.id] : [];
  const effectivePermissionPageId =
    parent?.effectivePermissionPageId ??
    (parent?.permissionMode === "restricted" ? parent.id : null);
  const optimisticNow = Timestamp.now();
  const page: DocumentationPage = {
    id: pageRef.id,
    groupId,
    parentPageId: input.parentPageId,
    ancestorIds,
    effectivePermissionPageId,
    title: input.title,
    normalizedTitle: normalizeText(input.title),
    icon: "📄",
    content: blankContent,
    plainTextContent: "",
    searchTokens: tokenizeSearch(input.title),
    depth,
    order: nextOrder,
    createdBy: actor.id,
    updatedBy: actor.id,
    createdAt: optimisticNow,
    updatedAt: optimisticNow,
    status: "draft",
    isFavoriteEligible: true,
    permissionMode: effectivePermissionPageId ? "restricted" : "inherit",
    version: 1,
  };
  const now = serverTimestamp();
  const batch = writeBatch(db);
  batch.set(pageRef, {
    ...page,
    createdAt: now,
    updatedAt: now,
  });
  addPageActivityToBatch(
    batch,
    db,
    groupId,
    pageRef.id,
    page.title,
    actor,
    "page.created",
  );
  await batch.commit();
  return page;
}

export async function renamePage(
  db: Firestore,
  groupId: string,
  pageId: string,
  actor: ActivityActor,
  title: string,
) {
  const batch = writeBatch(db);
  batch.update(doc(db, "groups", groupId, "pages", pageId), {
    title,
    normalizedTitle: normalizeText(title),
    searchTokens: tokenizeSearch(title),
    updatedBy: actor.id,
    updatedAt: serverTimestamp(),
  });
  addPageActivityToBatch(
    batch,
    db,
    groupId,
    pageId,
    title,
    actor,
    "page.renamed",
  );
  await batch.commit();
}

export async function savePageContent(
  db: Firestore,
  page: DocumentationPage,
  userId: string,
  content: RichTextDocument,
  plainTextContent: string,
  expectedVersion: number,
) {
  const pageRef = doc(db, "groups", page.groupId, "pages", page.id);
  const latest = await getDoc(pageRef);
  if (!latest.exists() || latest.data().version !== expectedVersion) {
    throw new Error("This page changed elsewhere. Refresh before saving again.");
  }

  const nextVersion = expectedVersion + 1;
  const versionRef = doc(
    collection(db, "groups", page.groupId, "pageVersions"),
  );
  const batch = writeBatch(db);
  batch.update(pageRef, {
    content,
    plainTextContent,
    searchTokens: tokenizeSearch(`${page.title} ${plainTextContent}`),
    version: nextVersion,
    updatedBy: userId,
    updatedAt: serverTimestamp(),
  });
  batch.set(versionRef, {
    id: versionRef.id,
    groupId: page.groupId,
    pageId: page.id,
    versionNumber: nextVersion,
    title: page.title,
    content,
    plainTextContent,
    createdBy: userId,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
  return nextVersion;
}

export async function setPageStatus(
  db: Firestore,
  groupId: string,
  pageId: string,
  actor: ActivityActor,
  status: "deleted" | "archived" | "draft" | "published",
  title: string,
) {
  const batch = writeBatch(db);
  batch.update(doc(db, "groups", groupId, "pages", pageId), {
    status,
    ...(status === "published"
      ? { publishedAt: serverTimestamp() }
      : status === "draft"
        ? { publishedAt: deleteField() }
        : {}),
    updatedAt: serverTimestamp(),
    updatedBy: actor.id,
  });
  addPageActivityToBatch(
    batch,
    db,
    groupId,
    pageId,
    title,
    actor,
    `page.${status}`,
  );
  await batch.commit();
}

export async function restorePage(
  db: Firestore,
  groupId: string,
  pageId: string,
  actor: ActivityActor,
) {
  const page = await getPage(db, groupId, pageId);
  if (!page) throw new Error("Page not found.");
  let parentPageId = page.parentPageId;
  if (parentPageId) {
    const parent = await getPage(db, groupId, parentPageId);
    if (!parent || parent.status === "deleted") parentPageId = null;
  }
  const batch = writeBatch(db);
  batch.update(doc(db, "groups", groupId, "pages", pageId), {
    parentPageId,
    status: "draft",
    updatedAt: serverTimestamp(),
    updatedBy: actor.id,
  });
  addPageActivityToBatch(
    batch,
    db,
    groupId,
    pageId,
    page.title,
    actor,
    "page.restored",
  );
  await batch.commit();
}

export async function permanentlyDeletePage(
  functions: Functions,
  groupId: string,
  pageId: string,
) {
  await httpsCallable(
    functions,
    "permanentlyDeletePage",
    { limitedUseAppCheckTokens: true },
  )({ groupId, pageId });
}

export async function movePage(
  functions: Functions,
  groupId: string,
  pageId: string,
  parentPageId: string | null,
  order: number,
) {
  await httpsCallable(
    functions,
    "movePage",
    { limitedUseAppCheckTokens: true },
  )({
    groupId,
    pageId,
    parentPageId,
    order,
  });
}

export async function duplicatePage(
  db: Firestore,
  page: DocumentationPage,
  actor: ActivityActor,
) {
  const created = await createPage(db, page.groupId, actor, {
    title: `${page.title} copy`,
    parentPageId: page.parentPageId,
  });
  await updateDoc(doc(db, "groups", page.groupId, "pages", created.id), {
    content: page.content,
    plainTextContent: page.plainTextContent,
    searchTokens: page.searchTokens,
  });
  return created.id;
}

export async function listPageVersions(
  db: Firestore,
  groupId: string,
  pageId: string,
) {
  const snapshot = await getDocs(
    query(
      collection(db, "groups", groupId, "pageVersions"),
      where("pageId", "==", pageId),
      orderBy("versionNumber", "desc"),
      limit(50),
    ),
  );
  return snapshot.docs.map((item) => item.data() as PageVersion);
}

export async function restoreVersion(
  db: Firestore,
  page: DocumentationPage,
  version: PageVersion,
  userId: string,
) {
  return savePageContent(
    db,
    page,
    userId,
    version.content,
    version.plainTextContent,
    page.version,
  );
}

export async function getPagePermission(
  db: Firestore,
  groupId: string,
  pageId: string,
) {
  const snapshot = await getDoc(
    doc(db, "groups", groupId, "pagePermissions", pageId),
  );
  return snapshot.exists() ? (snapshot.data() as PagePermission) : null;
}

export async function trackRecent(
  db: Firestore,
  userId: string,
  page: DocumentationPage,
) {
  await setDoc(doc(db, "users", userId, "recentPages", page.id), {
    userId,
    pageId: page.id,
    groupId: page.groupId,
    title: page.title,
    icon: page.icon,
    lastViewedAt: serverTimestamp(),
  });
}

export async function toggleFavorite(
  db: Firestore,
  userId: string,
  page: DocumentationPage,
  favorite: boolean,
) {
  const favoriteRef = doc(db, "users", userId, "favorites", page.id);
  if (!favorite) {
    await deleteDoc(favoriteRef);
    return;
  }
  await setDoc(favoriteRef, {
    userId,
    pageId: page.id,
    groupId: page.groupId,
    title: page.title,
    icon: page.icon,
    createdAt: serverTimestamp(),
  });
}

export async function isPageFavorite(
  db: Firestore,
  userId: string,
  pageId: string,
) {
  const favorite = await getDoc(doc(db, "users", userId, "favorites", pageId));
  return favorite.exists();
}
